import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import { getUIKitColorHSLA } from "@/helpers/style.helper.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeeActionsMenu } from "@/pages/employees/employee/components/EmployeeActionsMenu.tsx";
import { EmployeeGiveRecognitionButton } from "@/pages/employees/employee/components/EmployeeGiveRecognitionButton.tsx";
import { EmployeeRecognitionGiftModal } from "@/pages/employees/employee/widgets/EmployeeRecognitionGiftModal.tsx";
import { EmployeePersonalCard } from "@/pages/employees/employee/components/EmployeePersonalCard.tsx";
import {
  EMPLOYEES_LIST_REFERRER_KEY,
  EmployeePageSection,
} from "@/pages/employees/employee/employee-page.constants.ts";
import { RequestPositionEditModal } from "@/pages/recruit/hiring-plan/widgets/RequestPositionEditModal.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useEmployeeRecognitionsSearch } from "@/queries/employees/use-employee-recognitions.query.ts";
import { useEmployeePortalAccess } from "@/queries/preboarding/use-preboarding.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFieldTypes } from "@/types/custom-field.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  ApiEmployee,
  ApiEmployeeLifecycleStatus,
  getEmployeeAvatarUrl,
  getPortalAccessModeTitle,
  LifecycleStatusTexts,
  PortalAccessMode,
} from "@/types/employees/employee.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  Box,
  Button,
  DropdownMenu,
  EmojiDisplay,
  FontAwesomeIcon,
  LinkTo,
  ListOption,
  Page,
  PreviewContentModal,
  Stack,
  Tag,
  Text,
  Title,
  toCssColorValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import {
  createContext,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { EmployeeWorkflowRunModal } from "@/pages/employees/employee/widgets/EmployeeWorkflowRunModal.tsx";
import { PhotoViewerModal } from "@/components/photo-viewer/PhotoViewerModal.tsx";
import { EmployeeProfileSearch } from "@/pages/employees/employee/components/EmployeeProfileSearch.tsx";

interface Props {
  employee: ApiEmployee;
}

interface SidePanelScrollContextValue {
  isScrolled: boolean;
  avatarRef: RefObject<HTMLDivElement | null>;
}

const SidePanelScrollContext = createContext<SidePanelScrollContextValue | null>(null);

const useSidePanelScroll = () => {
  const ctx = useContext(SidePanelScrollContext);
  if (!ctx) throw new Error("useSidePanelScroll must be used within EmployeePageSidePanelProvider");
  return ctx;
};

export const EmployeePageSidePanelProvider = ({ children }: { children: ReactNode }) => {
  const avatarRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const el = avatarRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(([entry]) => setIsScrolled(!entry.isIntersecting), {
      threshold: 0,
      rootMargin: "-64px 0px 0px 0px",
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <SidePanelScrollContext.Provider value={{ isScrolled, avatarRef }}>
      {children}
    </SidePanelScrollContext.Provider>
  );
};

export const EmployeePageSidePanelHeader = ({ employee }: Props) => {
  const { t } = useTranslation();
  const { data: employeeMe } = useEmployeeMe();
  const { isScrolled } = useSidePanelScroll();
  const navigate = useNavigate();

  const handleBack = useCallback(() => {
    const referrer = sessionStorage.getItem(EMPLOYEES_LIST_REFERRER_KEY);
    sessionStorage.removeItem(EMPLOYEES_LIST_REFERRER_KEY);
    navigate(referrer || "/employees");
  }, [navigate]);

  return (
    <div className={isScrolled ? "shadow-md" : ""}>
      <Page.Header
        title={employeeMe?.id === employee.id ? t("core:profile") : ""}
        showBack={(employeeMe && location.pathname !== `/employees/${employeeMe.id}`) || false}
        backHandler={handleBack}
        className="border-r"
      >
        <Stack direction={"horizontal"} gap={"sm"}>
          <EmployeeProfileSearch employee={employee} />
          <EmployeeActionsMenu employee={employee} />
        </Stack>
      </Page.Header>

      {/* Grid rows trick: анимирует реальную высоту контента, без рывков */}
      <div
        className={twMerge(
          "grid transition-[grid-template-rows] duration-250 ease-out",
          isScrolled ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0 max-h-0"
        )}
      >
        <div className={"overflow-hidden px-6 py-4"}>
          <EmployeeListItem employee={employee} behavior={"card"} />
        </div>
      </div>
    </div>
  );
};

export const EmployeePageSidePanelBody = ({ employee }: Props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { openModal } = useModals();
  const { renderEmployeeName, renderShortDate } = useFormatter();
  const { renderPendingTooltipContent } = useCustomFieldsHook();
  const { data: employeeMe } = useEmployeeMe();
  const { section } = useParams();
  const { avatarRef } = useSidePanelScroll();

  const { canEditField, canViewField, canIEdit, canIView, isFeatureEnabled } = usePermissions();

  const canViewRecognitions =
    isFeatureEnabled(ApiFeatureType.recognitions) &&
    canViewField(employee, EmployeeFieldPermission.employee_recognitions);

  const { data: recognitionsData, isLoading: isRecognitionsLoading } =
    useEmployeeRecognitionsSearch(employee.id, { page_number: 1 }, canViewRecognitions);
  const recognitionBadges = recognitionsData?.items?.slice(0, 7).map((item) => item.recognition);
  const recognitionsCount = recognitionsData?.metadata?.total_items ?? 0;

  useEffect(() => {
    let tab: string;
    if (section == EmployeePageSection.positions) tab = "Positions";
    else if (section == EmployeePageSection.documents) tab = "Documents";
    else if (section == EmployeePageSection.timeOff) tab = "Time Off";
    else if (section == EmployeePageSection.positionRequests) tab = "Requests";
    else if (section == EmployeePageSection.feedbackEvaluation) tab = "Feedback & Evaluation";
    else tab = "Overview";

    analyticsService.trackEvent(analyticEvents.employees.viewedEmployee, {
      [analyticProperties.id]: id,
      [analyticProperties.source]: "Page",
      [analyticProperties.tab]: tab,
    });
  }, [id, section]);

  const canViewPositionRequestButton =
    isFeatureEnabled(ApiFeatureType.ats_hiring_plan) &&
    canIView(ApiPermission.recruiting_module) &&
    employeeMe?.id === employee?.id;

  const canViewTimeOffRequestButton =
    isFeatureEnabled(ApiFeatureType.time_off) &&
    (canIEdit(ApiPermission.approve_and_reject_leaves) ||
      canEditField(employee, EmployeeFieldPermission.employee_time_off));

  const hasCreationButtons = canViewPositionRequestButton || canViewTimeOffRequestButton;
  const employeePositionDetails = employee.employee_position;
  const probationEndDate = employee.employee_employment?.date_probation_ends;
  const hiredStartDate = employee?.date_hired_on;
  const now = new Date();
  const hasActiveProbation = !!probationEndDate && probationEndDate > now;
  const isNotStartedYet = !!hiredStartDate && hiredStartDate > now;
  const isTerminated = employee.lifecycle_status === ApiEmployeeLifecycleStatus.terminated;
  const showNotStartedTag = hasActiveProbation && isNotStartedYet;
  const showProbationTag = hasActiveProbation && !isNotStartedYet && !isTerminated;

  const canViewPortalAccess = canViewField(
    employee,
    EmployeeFieldPermission.employee_portal_access
  );
  const { data: portalAccess } = useEmployeePortalAccess(employee.id, canViewPortalAccess);
  const showPortalAccessTag =
    !employee.is_banned && !!portalAccess && portalAccess.mode !== PortalAccessMode.full;

  const handleViewFullDescription = useCallback(() => {
    const jobProfile = employee?.employee_position?.job_profile;
    if (!jobProfile?.description) return;

    openModal(PreviewContentModal, {
      modalTitle: getEmployeePositionTitle(employee),
      content: jobProfile.description,
    });
  }, [employee]);

  const employeePosition = useMemo(() => {
    return getEmployeePositionTitle(employee);
  }, [employee]);

  const namePendingChange = useMemo(() => {
    const nameFieldTypes: ApiFieldTypes[] = [
      ApiFieldTypes.employee_first_name,
      ApiFieldTypes.employee_last_name,
      ApiFieldTypes.employee_middle_name,
    ];
    const changes = employee.pending_changes?.filter((change) =>
      nameFieldTypes.includes(change.field.type)
    );

    if (!changes?.length) return undefined;

    const pendingEmployee = { ...employee };

    changes.forEach((change) => {
      const value = change.value_after?.value_text ?? "";

      if (change.field.type === ApiFieldTypes.employee_first_name)
        pendingEmployee.first_name = value;
      if (change.field.type === ApiFieldTypes.employee_last_name) pendingEmployee.last_name = value;
      if (change.field.type === ApiFieldTypes.employee_middle_name)
        pendingEmployee.middle_name = value;
    });

    return {
      currentName: renderEmployeeName(employee),
      newName: renderEmployeeName(pendingEmployee),
      createdAt: changes.reduce(
        (earliest, change) =>
          change.datetime_created_at < earliest ? change.datetime_created_at : earliest,
        changes[0].datetime_created_at
      ),
    };
  }, [employee, renderEmployeeName]);

  const employeeNameTitle = (
    <Title
      header={renderEmployeeName(employee)}
      size={"lg"}
      className={"text-center wrap-break-word"}
    />
  );

  const employeePendingNameTitle = namePendingChange?.newName && (
    <Tooltip>
      <TooltipTrigger>
        <Title
          header={`${namePendingChange.newName}...`}
          size={"lg"}
          className={"text-secondary text-center wrap-break-word"}
        />
      </TooltipTrigger>
      <TooltipContent>
        {renderPendingTooltipContent(namePendingChange.createdAt, {
          currentValue: namePendingChange.currentName,
          newValue: namePendingChange.newName,
        })}
      </TooltipContent>
    </Tooltip>
  );

  const openPhotoViewer = () => {
    if (!employee.avatar_url) return;

    const avatarURL = getEmployeeAvatarUrl(employee, "full");

    if (!avatarURL) return;

    openModal(
      PhotoViewerModal,
      {
        photos: [avatarURL],
      },
      {
        title: t("common:profile_photo"),
      }
    );
  };

  return (
    <>
      <Stack direction={"vertical"} gap={"md"} items={"center"}>
        <Box
          ref={avatarRef}
          className={twMerge("relative group bg-light rounded-lg cursor-pointer")}
          onClick={openPhotoViewer}
        >
          <EmployeeAvatar size={"2xl"} employee={employee} className={"border-3 border-light"} />
          {canViewRecognitions && (
            <EmployeeGiveRecognitionButton
              badges={recognitionBadges}
              count={recognitionsCount}
              isLoading={isRecognitionsLoading}
              onGive={
                recognitionsData?.can_gift
                  ? () => openModal(EmployeeRecognitionGiftModal, { employee })
                  : undefined
              }
              viewHref={`/employees/${employee.id}#section-recognitions`}
              className={"absolute -bottom-1.75 -right-4.25"}
            />
          )}
        </Box>
        <Stack items={"center"} gap={"md"} className={"pb-(--gap-lg)"}>
          <Stack items={"center"} justify={"center"} className={"w-full"}>
            {namePendingChange ? employeePendingNameTitle : employeeNameTitle}
            {employeePosition && (
              <Text className={"text-lg text-center break-words"}>
                {employeePosition}
                {employeePositionDetails?.job_profile?.description && (
                  <>
                    {"\u00A0"}
                    <button
                      type={"button"}
                      onClick={handleViewFullDescription}
                      aria-label={t("common:description")}
                      title={t("common:description")}
                      className={
                        "relative -top-px inline-flex align-middle cursor-pointer text-dark/75 hover:text-dark"
                      }
                    >
                      <FontAwesomeIcon icon={"fa-light fa-circle-info"} />
                    </button>
                  </>
                )}
              </Text>
            )}
            {employeePositionDetails?.department && (
              <Text className={"text-secondary text-center break-words"}>
                {employeePositionDetails.department.title}
              </Text>
            )}
          </Stack>

          {employee.lifecycle_status && (
            <Text
              className={"text-center font-mono font-medium uppercase leading-5 tracking-[0.07em]"}
              size="xs"
            >
              <span className={isTerminated ? "text-warning" : "text-positive"}>
                {LifecycleStatusTexts()[employee.lifecycle_status]}
              </span>{" "}
              {(isTerminated || employee.is_banned != null) && (
                <Text>
                  (
                  {t(
                    isTerminated || employee.is_banned
                      ? "core:lifecycle_inactive"
                      : "core:lifecycle_active"
                  )}
                  )
                </Text>
              )}
            </Text>
          )}

          {showNotStartedTag && (
            <Tag variant={"new"} className={"gap-1"}>
              <FontAwesomeIcon icon={"fa-light fa-hourglass"} />
              {t("core:employee_not_started_yet", { date: renderShortDate(hiredStartDate) })}
            </Tag>
          )}

          {showProbationTag && (
            <Tag variant={"warning"} className={"gap-1"}>
              <FontAwesomeIcon icon={"fa-light fa-timer"} />
              {`${t("core:on_probation_till")} ${renderShortDate(probationEndDate)}`}
            </Tag>
          )}

          {employee.time_off && (
            <Tag
              className={"gap-1"}
              borderColor={toCssColorValue(
                employee.time_off.category.color,
                getUIKitColorHSLA("brand-dark", 0.2)
              )}
              color={toCssColorValue(
                employee.time_off.category.color,
                getUIKitColorHSLA("brand-dark", 1)
              )}
            >
              <EmojiDisplay id={employee.time_off.category.emoji} size={"sm"} />

              {`${employee.time_off.category.title} ${t("common:till")} ${renderShortDate(employee.time_off.date_to)}`}
            </Tag>
          )}

          {showPortalAccessTag && (
            <Tooltip>
              <TooltipTrigger>
                <Tag variant={"warning"} size={"md"} className={"gap-1"}>
                  <FontAwesomeIcon icon={"fa-light fa-lock"} />
                  {getPortalAccessModeTitle(portalAccess.mode)}
                </Tag>
              </TooltipTrigger>
              <TooltipContent>
                <Text className={"text-sm"}>{t("core:portal_access_mode_hint")}</Text>
              </TooltipContent>
            </Tooltip>
          )}

          {hasCreationButtons && (
            <DropdownMenu trigger={<Button>{t("core:new_request")}</Button>}>
              {canViewTimeOffRequestButton ? (
                <LinkTo
                  to={`/time-off/request/new?employeeId=${employee.id}`}
                  className={"no-underline"}
                >
                  <ListOption
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-tree-palm"} />}
                    title={t("time_off:time_off")}
                    caption={t("time_off:vacancies_sick")}
                  />
                </LinkTo>
              ) : (
                ""
              )}
              {canViewPositionRequestButton && (
                <ListOption
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-user-plus"} />}
                  title={t("hiring_plan:position_request")}
                  caption={t("hiring_plan:new_employee_required")}
                  onClick={() => {
                    openModal(RequestPositionEditModal);
                  }}
                />
              )}

              {isFeatureEnabled(ApiFeatureType.workflows) && employee.can_run_shared_workflows && (
                <ListOption
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-ellipsis" />}
                  title={`${t("workflow:more_requests")}...`}
                  caption={t("workflow:benefits_and_approvals")}
                  onClick={() => {
                    openModal(EmployeeWorkflowRunModal, { employeeId: employee.id });
                  }}
                />
              )}
            </DropdownMenu>
          )}
        </Stack>
      </Stack>
      <EmployeePersonalCard employee={employee} />
    </>
  );
};
